/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'jquery',
	'underscore',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/OnLoadFilterModifier'
], ($, _, stringResources, APIFactory, OnLoadFilterModifierAPI) => {

	class OnLoadFilterModifier {
		constructor(options) {
			this.dashboard = options.features['API'];
			this.dataSources = options.features['DataSources'];
			this.moserDataSources = options.features['DataSources.moser'];
			this.globalFilters =  options.features['GlobalFilters'];
			this._api = APIFactory.createAPI(this, [OnLoadFilterModifierAPI]);
			this._itemIds = [];
			this.moserDataSources.registerMetadataSubsetIdsProvider(this._api);

			//Holds a set of all dataSources that we've called loadMetadata on so we try them first whenever possible.
			//See _getDataSourceListLoadedFirst()
			this._loadedDataSources = {};
			//If ANY filters were added by this modifier, mark it so that we set the dashboard as dirty.
			this.filterSet = false;
		}

		initialize() {
			const contentView = this.dashboard.getCurrentContentView();
			const sFilters = contentView.getContent().filters;

			if(!sFilters) {
				return Promise.resolve();
			}

			//Accept either a spec of form { options: {}, filters: [] } or a simple filters array.
			const filterSpec = JSON.parse(sFilters);
			const filters = filterSpec.filters ? filterSpec.filters : filterSpec;
			const drillThroughOptions = this._initializeDrillThroughOptions(filterSpec.options);
			return this._performDrillThrough(drillThroughOptions, filters);
		}

		//Default options to legacy drill through (assetId/columnId match only)
		_initializeDrillThroughOptions(drillThroughSpecOptions) {
			let drillThroughOptions = {
				overrideExistingFiltersOnly: false,
				matchMetadataAcrossSources: false
			};
			if (drillThroughSpecOptions) {
				Object.keys(drillThroughSpecOptions)
					.forEach((optionId) => drillThroughOptions[optionId] = drillThroughSpecOptions[optionId]);
			}
			return drillThroughOptions;
		}

		_performDrillThrough(drillThroughOptions, filters) {
			//First, match on assetId/columnId.  Apply options on the remaining unmatched filters
			return this._matchByAssetAndColumnId(filters, drillThroughOptions).then(({ errorThrown, unmatchedFilters }) => {
				if (errorThrown) {
					return unmatchedFilters;
				}
				return this._processDrillThroughOptions(drillThroughOptions, unmatchedFilters).then((finalUnmatchedFilters) => {
					if (!drillThroughOptions.overrideExistingFiltersOnly && finalUnmatchedFilters.length) {
						this._displayColumnNotFound();
					}
					if(this.filterSet) {
						this.dashboard.setDirty(true);
					}
				});
			});
		}

		/**
		 * @description Support the legacy drill through behaviour
		 *  - match by assetId and columnId (create filters that don't exist for matching target asset.)
		 * @param {*} filters - an array of filters that haven't been matched yet.
		 * @param {Object} drillThroughOptions
		 * @param {Boolean} drillThroughOptions.overrideExistingFiltersOnly - true if drill through should only SET filters, not create them.
		 * @param {Boolean} drillThroughOptions.matchMetadataAcrossSources - true if drill through should try to set or create filters in diferent modules.
		 * @returns An array of unmatched filters that remain after processing the matching assets/columns.
		 */
		_matchByAssetAndColumnId(filters, { overrideExistingFiltersOnly, matchMetadataAcrossSources }) {
			const loadedDataSources = {};
			const datasourceByAssetId = {};
			let errorThrown = false;
			this.dataSources.getDataSourceList().forEach(dataSource => {
				datasourceByAssetId[dataSource.getAssetId()] = dataSource;
			});
			const datasourceAssetIds = Object.keys(datasourceByAssetId);
			const sourceMatchedFilters = filters.filter(filter => {
				return datasourceAssetIds.includes(filter.assetId);
			});
			if(!matchMetadataAcrossSources && sourceMatchedFilters.length !== filters.length) {
				this.dashboard.showToast(stringResources.get('OnLoadFilterModifierDatasourceMismatch'), { type: 'info' });
				errorThrown = true;
			}
			if(!sourceMatchedFilters.length) {
				return Promise.resolve({ errorThrown, unmatchedFilters: filters });	//All the filters are still unmatched but subsequent processing might match them.
			}

			if (!overrideExistingFiltersOnly) {
				//If overriding existing filters only, no need to augment the subsetIds - they are already in the target.
				this._itemIds = sourceMatchedFilters.map(filter => filter.columnId).filter(Boolean);
			}

			const usedDataSourceAssetIds = _.uniq(sourceMatchedFilters.map(filter => filter.assetId));
			const loadMetadataPromises = [];
			usedDataSourceAssetIds.forEach(assetId => {
				const datasource = datasourceByAssetId[assetId];
				loadMetadataPromises.push(datasource.loadMetadata());
				loadedDataSources[datasource.getId()] = datasource;	//Once resolved, these DataSources will be loaded.
			});

			return Promise.all(loadMetadataPromises)
				.then(() => {
					sourceMatchedFilters.forEach(filter => {
						const scope = filter.scope || 'global';	//TODO: does anything but global make sense???
						const assetId = filter.assetId;
						const datasource = datasourceByAssetId[assetId];
						const globalFiltersForDataSource = this.globalFilters.getFilters(datasource.getId(), scope);
						if(datasource.getMetadataColumn(filter.columnId)) {
							//handle overrideExistingFiltersOnly for the non-cross datasource scenario.
							if (!overrideExistingFiltersOnly || (globalFiltersForDataSource && globalFiltersForDataSource.filterExists({ itemId: filter.columnId }))) {
								this._addFilter(filters, datasource.getId(), filter);
							}
						}
					});

					//The unmatched filters are the ones that aren't set to null.
					const unmatchedFilters = this._cleanUnmatchedFiltersList(filters);
					this._addToLoadedDataSources(loadedDataSources);

					return { unmatchedFilters };
				});
		}

		_processDrillThroughOptions({ overrideExistingFiltersOnly, matchMetadataAcrossSources }, unmatchedFilters) {
			if (matchMetadataAcrossSources) {
				if (!overrideExistingFiltersOnly) {
					//If we're matching existing filters only, the id's of the existing filters in the target
					//will already be in the metadata subset.  If not, we need to ensure ALL of the filter
					//columnIds are in the subset when we load any metadata.
					//Otherwise, loadMetadata/getMetadataColumn will not return them
					this._updateMetadataSubsetIds(unmatchedFilters);
				}
				return this._overrideExistingFilters(unmatchedFilters).then((remainingUnmatchedFilters) => {
					if (remainingUnmatchedFilters.length) {
						if (!overrideExistingFiltersOnly) {
							//If there are any filters left over, match across dataSources (if the option is set)
							return this._matchMetadataAcrossSources(remainingUnmatchedFilters);
						}
					}
					return Promise.resolve(remainingUnmatchedFilters);
				});
			}
			return Promise.resolve(unmatchedFilters);
		}

		/**
		 * @description Support the match existing only option for the cross-datasource case.  ie: only SET an existing filter in the target
		 * ie: if the filter dock already has a global filter on a specific columnId, set it.
		 * @param [Object] filters - an array of filters that haven't been matched yet.
		 * @returns An array of unmatched filters that remain after processing all dataSources.
		 */
		_overrideExistingFilters(filters) {
			const loadMetadataPromises = [];
			const existingFiltersByDataSource = {};
			const loadedDataSources = {};

			this._getDataSourceListLoadedFirst().forEach((dataSource) => {
				filters.forEach((filter) => {
					const scope = filter.scope || 'global';	//TODO: does anything but global make sense???
					const globalFiltersForDataSource = this.globalFilters.getFilters(dataSource.getId(), scope);
					const columnId = filter.columnId;
					//TODO: should do a dataFilters version of this.
					if (globalFiltersForDataSource && globalFiltersForDataSource.filterExists({ itemId: columnId })) {
						if (!existingFiltersByDataSource[dataSource.getId()]) {
							//Ensure metadata is loaded before call to addFilter() - it calls getMetadataColumn() (see Filters.js)
							//NOTE: We could potentially avoid loading metadata when modifying existing filters if we changed addFilter()
							//		to alow us to set everything (dataType, usage, hierarchy etc) from what's in the existing entry.
							existingFiltersByDataSource[dataSource.getId()] = [filter];
							//If we load metadata, we need to ensure the metadataSubsetIds are included
							loadMetadataPromises.push(dataSource.loadMetadata());
							loadedDataSources[dataSource.getId()]=dataSource;
						} else {
							existingFiltersByDataSource[dataSource.getId()].push(filter);
						}
					}
				});
			});

			//NOTE: Since we're just modifying existing filters, we can assume no metadataSubsetIds need to be added for matchExisting case.

			return Promise.all(loadMetadataPromises)
				.then(() => {
					Object.keys(existingFiltersByDataSource).forEach((dataSourceId) => {
						const filtersToAdd = existingFiltersByDataSource[dataSourceId];
						filtersToAdd.forEach((filterToAdd) => {
							this._addFilter(filters, dataSourceId, filterToAdd);
						});
					});
					this._addToLoadedDataSources(loadedDataSources);
					return this._cleanUnmatchedFiltersList(filters);
				});
		}

		_updateMetadataSubsetIds(filterList) {
			filterList.forEach((filter) => {
				if (this._itemIds.indexOf(filter.columnId)===-1) {
					this._itemIds.push(filter.columnId);
				}
			});
		}

		/**
		 * @description Support the match across data sources option.  ie: Set or create filters in the target by
		 * recursively trying all dataSources (starting with the loaded ones) to find the first match for the source filter (ie: same columnId)
		 * @param {*} unmatchedList - an array of filters that haven't been matched yet.
		 * @returns An array of unmatched filters that remain after processing all dataSources.
		 */
		_matchMetadataAcrossSources(unmatchedList) {
			//This function should only run if the option was chosen and there are filters left to match.
			return this._processDataSource(0, unmatchedList);
		}

		_processDataSource(dataSourceIdx, unmatchedList) {
			//TODO: process this list using the loadedDataSources first, then the others.
			const dataSourcesCount = this.dataSources.getDataSourceList().length;
			if (dataSourceIdx < dataSourcesCount && unmatchedList.length) {
				const dataSource = 	this._getDataSourceListLoadedFirst()[dataSourceIdx];
				return dataSource.loadMetadata().then(() => {
					//Try to find any filter that exists in that datasource
					//and, if found, remove it from the list before moving on.
					unmatchedList.forEach((filterToMatch) => {
						const mdColumn = dataSource.getMetadataColumn(filterToMatch.columnId);
						if (mdColumn && !mdColumn.isHidden()) {
							//console.log('found', filterToMatch.columnId, 'in dataSource:', dataSource.getId(), 'index', dataSourceIdx, '=>', dataSource.getAssetId());
							this._addFilter(unmatchedList, dataSource.getId(), filterToMatch);
						}
					});
					unmatchedList = this._cleanUnmatchedFiltersList(unmatchedList);
					if (unmatchedList.length) {
						return this._processDataSource(dataSourceIdx + 1, unmatchedList);
					}
					return Promise.resolve(unmatchedList);
				});
			}
			return Promise.resolve(unmatchedList);
		}

		_addFilter(filters, dataSourceId, filterToAdd) {
			const scope = filterToAdd.scope || 'global'; // ShareUrlGlobalFilters extensions may pass tab scope
			const columnId = filterToAdd.columnId;
			delete filterToAdd.scope;
			delete filterToAdd.assetId;
			this.globalFilters.removeFilters({ itemId: columnId, scope });
			this.globalFilters.getFilters(dataSourceId, scope)
				.addFilter({ itemId: columnId }, filterToAdd );
			this.filterSet = true;

			//Go back to the initial filters list and mark the filter as matched by setting it to null;
			const filterIdx = filters.findIndex((f) => f===filterToAdd);
			filters[filterIdx] = null;
		}

		_addToLoadedDataSources(loadedDataSources) {
			//For best performance, if a step completes and it has loaded metadata for any dataSources,
			//it saves it to a central set so we process those dataSources first in subsequent steps (see _getDataSourceListLoadedFirst())
			Object.keys(loadedDataSources).forEach((loadedDataSourceId) => this._loadedDataSources[loadedDataSourceId] = loadedDataSources[loadedDataSourceId]);
		}

		_getDataSourceListLoadedFirst() {
			//Return the dataSource list in "loaded first order".
			return [
				...this.dataSources.getDataSourceList().filter((dataSource) => this._loadedDataSources[dataSource.getId()] ),
				...this.dataSources.getDataSourceList().filter((dataSource) => this._loadedDataSources[dataSource.getId()]===undefined )
			];
		}

		_cleanUnmatchedFiltersList(filters) {
			return filters.filter((f) => f!==null);
		}

		getMetadataSubsetIds() {
			return this._itemIds;
		}

		_displayColumnNotFound() {
			this.dashboard.showToast(stringResources.get('OnLoadFilterModifierColumnMismatch'), { type: 'info' });
		}

		getAPI() {
			return this._api;
		}

	}

	return OnLoadFilterModifier;
});
